/**
 * Head-to-Head challenge page.
 * - If challenge is `awaiting_opponent`: show owner's squad summary + a
 *   simplified slot-by-slot wheel for the opponent to build their XI.
 * - Once answered (status=complete): render both squads + winner banner.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft, Crown, Trophy, ArrowsClockwise, Spinner, X, Lightning,
    SoccerBall,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POS_COLOR = {
    GKP: "text-fa-cream",  DEF: "text-fa-mint",
    MID: "text-fa-sky",    FWD: "text-fa-yellow",
};

function buildSlots(formation) {
    const order = ["GKP", "DEF", "MID", "FWD"];
    const out = []; let idx = 0;
    for (const pos of order) {
        for (let i = 0; i < (formation[pos] || 0); i++) {
            out.push({ idx, pos });
            idx += 1;
        }
    }
    return out;
}

function SquadList({ title, squad, captainId, accentClass, testid }) {
    return (
        <div className={`border ${accentClass} p-3 space-y-2`} data-testid={testid}>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1.5">
                <Crown size={11} weight="fill" /> {title}
            </div>
            <div className="space-y-1">
                {squad.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 font-mono text-[11px] border-b border-white/5 pb-1">
                        <span className={`font-mono text-[9px] uppercase tracking-widest w-7 ${POS_COLOR[p.position] || "text-white/50"}`}>{p.position}</span>
                        <span className="font-display font-black truncate flex-1">
                            {p.name}
                            {p.id === captainId && <span className="font-mono text-[8px] font-bold ml-1 text-fa-yellow">(C)</span>}
                        </span>
                        <span className="font-mono text-[10px] text-white/45">{p.team_name}</span>
                        <span className="font-mono text-[10px] tabular-nums text-white/70">£{p.price}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function LineupSimH2H() {
    const { challenge_id } = useParams();
    const [challenge, setChallenge] = useState(null);
    const [err, setErr] = useState(null);
    const [squad, setSquad] = useState({}); // slot idx -> player
    const [captainId, setCaptainId] = useState(null);
    const [opponentName, setOpponentName] = useState("");
    const [wheel, setWheel] = useState({ open: false, slot: null, candidates: [], busy: false });
    const [submitting, setSubmitting] = useState(false);

    const refresh = () => {
        axios.get(`${API}/lineup-sim/h2h/${challenge_id}`)
            .then((r) => setChallenge(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Challenge not found"));
    };
    useEffect(refresh, [challenge_id]);

    const formation = challenge?.formation;
    const SLOTS = useMemo(() => formation ? buildSlots(formation) : [], [formation]);
    const ownerOccupied = useMemo(() => new Set(challenge?.owner_player_ids || []), [challenge]);
    const filledIds = useMemo(() => Object.values(squad).filter(Boolean).map((p) => p.id), [squad]);
    const teamCounts = useMemo(() => {
        const tc = {};
        Object.values(squad).forEach((p) => { if (p) tc[p.team_id] = (tc[p.team_id] || 0) + 1; });
        return tc;
    }, [squad]);
    const spent = Object.values(squad).filter(Boolean).reduce((a, p) => a + p.price, 0);
    const remaining = challenge ? Math.round((challenge.budget - spent) * 10) / 10 : 0;
    const filledCount = Object.values(squad).filter(Boolean).length;
    const isValid = SLOTS.length > 0 && filledCount === SLOTS.length && spent <= (challenge?.budget || 100);

    const spinWheel = async (slot) => {
        if (!challenge) return;
        setWheel({ open: true, slot, candidates: [], busy: true });
        const t0 = Date.now();
        try {
            const r = await axios.post(`${API}/lineup-sim/wheel`, {
                mode: challenge.mode,
                slot_position: slot.pos,
                count: 4,
                excluded_ids: filledIds,
                team_counts: Object.fromEntries(Object.entries(teamCounts).map(([k, v]) => [String(k), v])),
                budget_left: remaining,
                difficulty: "standard",
                premium_bias: 0.10,
            });
            const elapsed = Date.now() - t0;
            if (elapsed < 900) await new Promise((res) => setTimeout(res, 900 - elapsed));
            setWheel({ open: true, slot, candidates: r.data.candidates, busy: false });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Wheel failed");
            setWheel({ open: false, slot: null, candidates: [], busy: false });
        }
    };

    const pickCand = (cand) => {
        setSquad((s) => ({ ...s, [wheel.slot.idx]: cand }));
        setWheel({ open: false, slot: null, candidates: [], busy: false });
    };

    const swapSlot = (idx) => {
        setSquad((s) => {
            const next = { ...s };
            if (s[idx]?.id === captainId) setCaptainId(null);
            delete next[idx];
            return next;
        });
    };

    const submitAnswer = async () => {
        if (!isValid) {
            toast.error("Fill all slots within budget first");
            return;
        }
        setSubmitting(true);
        try {
            const r = await axios.post(`${API}/lineup-sim/h2h/${challenge_id}/answer`, {
                opponent_name: opponentName || "Manager B",
                player_ids: SLOTS.map((s) => squad[s.idx].id),
                captain_id: captainId,
            }, { timeout: 60000 });
            toast.success(r.data.winner === "owner" ? "You lost" : r.data.winner === "opponent" ? "You won!" : "Draw");
            setChallenge((c) => ({ ...c, ...r.data }));
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (err) {
        return (
            <AppShell>
                <div className="border border-red-400/30 bg-red-400/5 p-4 font-mono text-[11px] text-red-300"
                     data-testid="h2h-error">{err}</div>
            </AppShell>
        );
    }
    if (!challenge) {
        return (
            <AppShell>
                <div className="font-mono text-[11px] text-white/40 flex items-center gap-2">
                    <Spinner size={12} weight="bold" className="animate-spin" /> Loading challenge…
                </div>
            </AppShell>
        );
    }

    const isComplete = challenge.status === "complete";
    const winner = challenge.winner;

    return (
        <AppShell>
            <section className="space-y-4" data-testid="h2h-page">
                <Link to="/lineup-sim" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft size={11} weight="bold" /> Back to Lineup Sim
                </Link>
                <header className="border-2 border-fa-yellow/40 bg-gradient-to-br from-fa-yellow/10 via-transparent to-fa-mint/5 p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Lightning size={11} weight="fill" /> Head-to-head challenge
                    </div>
                    <h1 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-1">
                        {challenge.owner_name} <span className="text-white/40">vs</span>{" "}
                        <span className="text-fa-yellow">
                            {isComplete ? challenge.opponent_name : "you"}
                        </span>
                    </h1>
                    <div className="font-mono text-[10px] text-white/55 mt-1">
                        {challenge.mode} mode · {challenge.n_gws} GWs · £{challenge.budget}m budget · formation {Object.entries(challenge.formation).map(([k,v])=>`${k.slice(0,1)}${v}`).join("·")}
                    </div>
                </header>

                {isComplete ? (
                    <>
                        <div className={`border-2 p-4 ${winner === "draw" ? "border-white/30 bg-white/[0.04]" : winner === "owner" ? "border-fa-yellow bg-fa-yellow/10" : "border-fa-mint bg-fa-mint/10"}`}
                             data-testid="h2h-winner">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 flex items-center gap-2">
                                <Trophy size={11} weight="fill" /> Result · diff {challenge.diff}p
                            </div>
                            <div className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                                {winner === "draw" ? "Draw" : (
                                    <><span className={winner === "owner" ? "text-fa-yellow" : "text-fa-mint"}>
                                        {winner === "owner" ? challenge.owner_name : challenge.opponent_name}
                                    </span> wins</>
                                )}
                            </div>
                            <div className="font-mono text-[11px] text-white/60 mt-2">
                                {challenge.owner_name}: <strong className="text-fa-yellow">{challenge.owner_result?.total?.avg}p</strong>
                                {" "}·{" "}
                                {challenge.opponent_name}: <strong className="text-fa-mint">{challenge.opponent_result?.total?.avg}p</strong>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <SquadList title={challenge.owner_name} testid="h2h-owner-squad"
                                       squad={challenge.owner_squad || []}
                                       captainId={challenge.owner_captain_id}
                                       accentClass={winner === "owner" ? "border-fa-yellow bg-fa-yellow/5" : "border-white/10 bg-[#0d0d0d]"}/>
                            <SquadList title={challenge.opponent_name} testid="h2h-opp-squad"
                                       squad={(challenge.opponent_player_ids || []).map((pid) => {
                                           const players = challenge.opponent_result?.players || [];
                                           return players.find((p) => p.id === pid) || { id: pid, name: "?", position: "?", team_name: "?", price: 0 };
                                       })}
                                       captainId={challenge.opponent_captain_id}
                                       accentClass={winner === "opponent" ? "border-fa-mint bg-fa-mint/5" : "border-white/10 bg-[#0d0d0d]"}/>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Owner's squad preview (read-only) */}
                        <SquadList title={`${challenge.owner_name}'s squad`}
                                   testid="h2h-owner-preview"
                                   squad={challenge.owner_squad || []}
                                   captainId={null}  // hidden until match runs
                                   accentClass="border-white/10 bg-[#0d0d0d]"/>

                        <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-3" data-testid="h2h-builder">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <input type="text" value={opponentName}
                                       onChange={(e) => setOpponentName(e.target.value)}
                                       placeholder="Your name (optional)"
                                       data-testid="h2h-opp-name"
                                       className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"/>
                                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest">
                                    <span className="text-white/40">Spent</span>
                                    <span className="text-white tabular-nums">£{spent.toFixed(1)}m</span>
                                    <span className="text-white/40">Left</span>
                                    <span className={`tabular-nums ${remaining < 0 ? "text-red-300" : "text-fa-mint"}`}>£{remaining.toFixed(1)}m</span>
                                </div>
                            </div>
                            {/* Slots */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {SLOTS.map((slot) => {
                                    const p = squad[slot.idx];
                                    return p ? (
                                        <div key={slot.idx} className="border border-fa-mint/30 bg-fa-mint/5 px-2 py-1.5 relative group">
                                            <div className="font-display font-black text-[13px] truncate">{p.name}</div>
                                            <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center justify-between">
                                                <span>{p.team_name} · {p.position}</span>
                                                <span className="text-white/70">£{p.price}</span>
                                            </div>
                                            <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/70">
                                                <button type="button" onClick={() => setCaptainId(p.id === captainId ? null : p.id)}
                                                        className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${p.id === captainId ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/40 text-white"}`}>
                                                    {p.id === captainId ? "(C)" : "Make C"}
                                                </button>
                                                <button type="button" onClick={() => swapSlot(slot.idx)}
                                                        className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-red-300/60 text-red-300">
                                                    <X size={9} weight="bold" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button key={slot.idx} type="button" onClick={() => spinWheel(slot)}
                                                data-testid={`h2h-slot-empty-${slot.pos}-${slot.idx}`}
                                                className="border-2 border-dashed border-white/20 hover:border-fa-yellow bg-black/30 px-2 py-1.5 flex flex-col items-center gap-0.5">
                                            <ArrowsClockwise size={14} weight="bold" className="text-fa-yellow animate-pulse" />
                                            <span className="font-mono text-[9px] uppercase tracking-widest font-bold text-fa-yellow">
                                                Spin {slot.pos}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <button type="button" onClick={submitAnswer}
                                    disabled={!isValid || submitting}
                                    data-testid="h2h-submit"
                                    className="w-full bg-fa-yellow text-black font-display font-black text-base uppercase tracking-tight py-2.5 hover:bg-fa-yellowHover disabled:opacity-40 flex items-center justify-center gap-2">
                                {submitting
                                    ? <><Spinner size={14} weight="bold" className="animate-spin"/> Simulating both squads…</>
                                    : isValid
                                        ? <><Lightning size={14} weight="fill"/> Lock in & duel</>
                                        : <>Fill {SLOTS.length - filledCount} more slot(s)</>}
                            </button>
                        </div>
                    </>
                )}
            </section>

            {/* Wheel modal */}
            {wheel.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                     onClick={() => setWheel({ open: false, slot: null, candidates: [], busy: false })}>
                    <div className="border-2 border-fa-yellow bg-[#0d0d0d] max-w-2xl w-full p-4 space-y-3"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                            <ArrowsClockwise size={12} weight="fill" /> Pick your {wheel.slot?.pos}
                        </div>
                        {wheel.busy ? (
                            <div className="font-mono text-[11px] text-white/60 flex items-center gap-2 py-8 justify-center">
                                <Spinner size={14} weight="bold" className="animate-spin" /> Spinning…
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {wheel.candidates.map((c) => (
                                    <button key={c.id} type="button" onClick={() => pickCand(c)}
                                            data-testid={`h2h-wheel-pick-${c.id}`}
                                            className="border border-white/15 hover:border-fa-yellow p-3 text-left">
                                        <div className="flex justify-between mb-1">
                                            <span className="font-display font-black text-base">{c.name}</span>
                                            <span className="font-mono text-[11px] text-fa-yellow font-bold tabular-nums">£{c.price}</span>
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                                            {c.team_name} · {c.position}
                                            {c.ownership != null && <span className="ml-2 text-white/35">{c.ownership.toFixed(1)}%</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </AppShell>
    );
}
